import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, CheckCircle, Circle, DollarSign, Package, Loader2 } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface UpsellTrackerProps {
    client: CJMSClient;
}

const UPSELL_PRODUCTS = [
    { id: 'domain', label: 'Custom Domain', price: 'R197/yr', desc: 'Professional .co.za domain hosted in Cloudflare' },
    { id: 'email', label: 'Business Email', price: 'R97/yr', desc: 'Professional email address matching your domain' },
    { id: 'gbp', label: 'Google Business Profile', price: 'R497/setup', desc: 'Complete GBP setup and optimization' },
    { id: 'website_builder', label: 'Website Builder', price: 'R397/mo', desc: 'Full website builder with AI-powered tools' },
    { id: 'bizbuilding_call', label: 'BizBuilding Discovery Call', price: 'Free', desc: 'Strategic session to map your growth roadmap' },
];

export const UpsellTracker: React.FC<UpsellTrackerProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Growth' || client.currentStage === 'Advocacy';
    const [upsellStatus, setUpsellStatus] = useState<Record<string, 'pending' | 'presented' | 'accepted' | 'declined'>>(
        Object.fromEntries(UPSELL_PRODUCTS.map(p => [p.id, 'pending']))
    );
    const [status, setStatus] = useState<'idle' | 'saving'>('idle');

    if (!isEligible) return null;

    const getStatusColor = (s: string) => {
        switch (s) {
            case 'accepted': return 'bg-green-50 border-green-200 text-green-700';
            case 'presented': return 'bg-amber-50 border-amber-200 text-amber-700';
            case 'declined': return 'bg-red-50 border-red-200 text-red-700';
            default: return 'bg-white border-slate-100 text-slate-600';
        }
    };

    const getStatusIcon = (s: string) => {
        switch (s) {
            case 'accepted': return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'presented': return <Circle className="w-4 h-4 text-amber-500" />;
            case 'declined': return <Circle className="w-4 h-4 text-red-500" />;
            default: return <Circle className="w-4 h-4 text-slate-300" />;
        }
    };

    const handleStatusChange = (id: string, newStatus: 'pending' | 'presented' | 'accepted' | 'declined') => {
        setUpsellStatus(prev => ({ ...prev, [id]: newStatus }));
        feedbackBus.emit('activity_completed', {
            activityLabel: `Upsell ${id} marked as ${newStatus}`,
            clientEmail: client.email,
        });
    };

    const acceptedCount = Object.values(upsellStatus).filter(s => s === 'accepted').length;
    const totalRevenue = UPSELL_PRODUCTS.filter(p => upsellStatus[p.id] === 'accepted')
        .reduce((sum, p) => sum + parseInt(p.price.replace(/[^0-9]/g, '')), 0);

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-orange-600" />
                    <h3 className="text-sm font-bold text-slate-800">Upsell Tracker</h3>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-500">{acceptedCount} accepted</span>
                    {totalRevenue > 0 && (
                        <span className="text-[10px] font-bold text-green-600">R{totalRevenue.toLocaleString()}/mo</span>
                    )}
                </div>
            </div>
            <div className="p-4 space-y-2">
                {UPSELL_PRODUCTS.map(product => (
                    <div key={product.id} className={`flex items-center justify-between p-3 rounded-lg border transition-all ${getStatusColor(upsellStatus[product.id])}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            {getStatusIcon(upsellStatus[product.id])}
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <Package className="w-3 h-3 text-slate-400" />
                                    <span className="text-xs font-semibold">{product.label}</span>
                                    <span className="text-[10px] font-medium text-orange-600">{product.price}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 mt-0.5">{product.desc}</p>
                            </div>
                        </div>
                        <select
                            value={upsellStatus[product.id]}
                            onChange={e => handleStatusChange(product.id, e.target.value as any)}
                            className="text-[10px] border border-slate-200 rounded px-2 py-1 focus:ring-1 focus:ring-orange-400 outline-none ml-2"
                        >
                            <option value="pending">Pending</option>
                            <option value="presented">Presented</option>
                            <option value="accepted">Accepted</option>
                            <option value="declined">Declined</option>
                        </select>
                    </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-4">
                    <div className="flex items-center gap-1 text-[10px] text-slate-500">
                        <DollarSign className="w-3 h-3" />
                        Potential additional revenue: R{(197 + 97 + 497 + 397).toLocaleString()}/yr setup + R397/mo ongoing
                    </div>
                </div>
            </div>
        </motion.div>
    );
};
